import React, { useState, useRef, useEffect, KeyboardEvent, ReactNode, forwardRef, HTMLAttributes, useImperativeHandle } from 'react';
import classNames from 'classnames';
interface DropdownOptions {
  label: React.ReactNode;
  value: string;
  hasPrefix?: React.ReactNode;
  hasSuffix?: React.ReactNode;
  description?: React.ReactNode;
  dividerAfter?: boolean;
  danger?: boolean;
  icon?: React.ReactNode;
}
interface DropdownProps extends Omit<HTMLAttributes<HTMLDivElement>, 'onSelect'> {
  options: DropdownOptions[];
  selectedOption?: string;
  onOptionSelect: (option: DropdownOptions) => void;
  onClose?: () => void;
  className?: string;
  children?: ReactNode;
  onEscape?: () => void;
}
const Dropdown = forwardRef<HTMLDivElement, DropdownProps>(({
  options,
  selectedOption,
  onOptionSelect,
  onClose,
  className,
  children,
  onEscape,
  ...props
}, ref) => {
  const [focusedIndex, setFocusedIndex] = useState<number | null>(null);
  const [typedChars, setTypedChars] = useState<string>('');
  const internalRef = useRef<HTMLDivElement>(null);
  const typingTimeoutRef = useRef<number | null>(null);
  useImperativeHandle(ref, () => internalRef.current as HTMLDivElement);
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (internalRef.current && !internalRef.current.contains(event.target as Node)) {
        if (onClose) onClose();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [onClose]);
  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (focusedIndex === null) {
      setFocusedIndex(0);
    } else {
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        setFocusedIndex(prevIndex => prevIndex !== null && prevIndex < options.length - 1 ? prevIndex + 1 : 0);
      } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        setFocusedIndex(prevIndex => prevIndex !== null && prevIndex > 0 ? prevIndex - 1 : options.length - 1);
      } else if (event.key === 'Enter' && focusedIndex !== null) {
        onOptionSelect(options[focusedIndex]);
      } else if (/^[a-zA-Z0-9]$/.test(event.key)) {
        setTypedChars(prevChars => prevChars + event.key);
      }
    }
    if (event.key === 'Escape') {
      if (onEscape) onEscape();
    }
  };
  const handleOptionClick = (option: DropdownOptions) => {
    onOptionSelect(option);
  };
  const handleOptionMouseEnter = (index: number) => {
    setFocusedIndex(index);
  };
  useEffect(() => {
    if (internalRef.current && focusedIndex !== null) {
      const focusedOption = internalRef.current.querySelectorAll<HTMLElement>('.option')[focusedIndex];
      if (focusedOption) {
        focusedOption.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest'
        });
        focusedOption.focus();
      }
    }
  }, [focusedIndex]);
  useEffect(() => {
    if (typedChars.length > 0) {
      const matchIndex = options.findIndex(option => option.value.toLowerCase().startsWith(typedChars.toLowerCase()));
      if (matchIndex !== -1) {
        setFocusedIndex(matchIndex);
      }
      if (typingTimeoutRef.current !== null) {
        clearTimeout(typingTimeoutRef.current);
      }
      typingTimeoutRef.current = window.setTimeout(() => {
        setTypedChars('');
      }, 500);
    }
  }, [typedChars, options]);
  useEffect(() => {
    if (internalRef.current && selectedOption !== undefined) {
      const selectedIndex = options.findIndex(option => option.value === selectedOption);
      if (selectedIndex !== -1) {
        setFocusedIndex(selectedIndex);
        const selectedOptionElement = internalRef.current.querySelectorAll<HTMLElement>('.option')[selectedIndex];
        if (selectedOptionElement) {
          selectedOptionElement.scrollIntoView({
            behavior: 'auto',
            block: 'nearest'
          });
        }
      }
    }
  }, [selectedOption, options]);
  return <div className={classNames("flex flex-col bg-white dark:bg-zinc-950 border border-neutral-300 dark:border-neutral-600 rounded-2xl shadow-lg p-2 overflow-y-auto min-w-[12rem]", className)} tabIndex={0} onKeyDown={handleKeyDown} ref={internalRef} role="listbox" {...props}>
            {children}
            {options.map((option, index) => <React.Fragment key={index}>
                    <div className={classNames("flex items-center p-2 cursor-pointer transition-colors duration-200 rounded-2xl", {
        "bg-neutral-100 dark:bg-neutral-900 font-regular": selectedOption === option.value,
        "hover:bg-neutral-200 dark:hover:bg-neutral-800": selectedOption !== option.value,
        "text-red-600 hover:bg-red-100": option.danger
      })} onClick={() => handleOptionClick(option)} onMouseEnter={() => handleOptionMouseEnter(index)} role="option" aria-selected={selectedOption === option.value} tabIndex={-1}>
                        {option.icon && <span className="mr-2">{option.icon}</span>}
                        {option.hasPrefix && <span className="mr-2">{option.hasPrefix}</span>}
                        <div className="flex-1">
                            <span className="text-sm font-medium text-neutral-800 dark:text-neutral-200">{option.label}</span>
                            {option.description && <span className="block text-xs text-neutral-500">{option.description}</span>}
                        </div>
                        {option.hasSuffix && <span className="ml-2">{option.hasSuffix}</span>}
                    </div>
                    {option.dividerAfter && <div className="border-t border-neutral-200 dark:border-neutral-600 my-2" />}
                </React.Fragment>)}
        </div>;
});
Dropdown.displayName = 'Dropdown';
export { Dropdown };
export type { DropdownOptions, DropdownProps };